import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ModalForm from '../Reusable/ModalForm';
import { SubmitButton } from '../../enums/GlobalEnums';
import { ModalForAddOrUpdateVehicle } from '../../enums/VehicleEnums';
import { findAllBrand } from '../../actions/brand';
import dayjs from 'dayjs';
import { findVehicleModelByBrandId } from '../../actions/vehicleModel';
import { findAllUsers } from '../../actions/user';

const ModalForAddVehicle = ({
  show,
  handleClose,
  handleAdd,
  vehicleReducer,
  brandReducer,
  userReducer,
  vehicleModelReducer,
}) => {
  const dispatch = useDispatch();
  const { error } = vehicleReducer;
  const { brandList } = brandReducer;
  const { userList } = userReducer;
  const { vehicleModelList } = vehicleModelReducer;
  const securityReducer = useSelector((state) => state.security);
  const { loggedUser } = securityReducer;

  const [selectedBrandId, setSelectedBrandId] = useState('');

  useEffect(() => {
    if (show) {
      dispatch(findAllBrand());

      dispatch(findAllUsers());
    }
  }, [dispatch, show]);

  useEffect(() => {
    if (selectedBrandId) {
      dispatch(findVehicleModelByBrandId(selectedBrandId));
    }
  }, [dispatch, selectedBrandId]);

  const fields = [
    {
      name: 'vehicleName',
      label: ModalForAddOrUpdateVehicle.vehicleNamePlaceholder,
      type: 'text',
    },
    {
      name: 'driver.id',
      label: 'Izaberi vozača',
      type: 'select',
      options: userList?.map((d) => ({
        value: d.id,
        label: d.username,
      })),
    },
    {
      name: 'brand.id',
      label: 'Brend',
      type: 'select',
      options: brandList?.map((brand) => ({
        value: brand.id,
        label: brand.brandName,
      })),
      onChange: (value) => {
        setSelectedBrandId(value);
      },
    },
    {
      name: 'vehicleModel.id',
      label: 'Izaberi model',
      type: 'select',
      options: vehicleModelList?.map((m) => ({
        value: m.id,
        label: m.modelName,
      })),
    },
    {
      name: 'licensePlateNumber',
      label: 'Registarski broj',
      type: 'text',
      placeholder: 'AA123AA ili AA1234AA',
    },
    {
      name: 'chassisNumber',
      label: 'Broj šasije',
      type: 'text',
    },
    {
      name: 'vehicleType',
      label: 'Tip vozila',
      type: 'select',
      options: [
        { value: 'Putničko vozilo', label: 'Putničko vozilo' },
        { value: 'Teretno vozilo', label: 'Teretno vozilo' },
        { value: 'Terensko vozilo', label: 'Terensko vozilo' },
      ],
    },

    {
      name: 'maximumAuthorisedMass',
      label: 'Maksimalna dozvoljena masa',
      type: 'number',
    },
    {
      name: 'fuelConsumptionPer100Kilometers',
      label: 'Potrosnja na 100km',
      type: 'number',
    },
    {
      name: 'mass',
      label: 'Masa',
      type: 'number',
    },
    {
      name: 'firstRegistration',
      label: 'Datum prve registracije',
      type: 'date',
      maxDate: dayjs(),
      // minDate: dayjs().add(1, 'day'),
    },
    {
      name: 'expiryDateOfLicensePlateNumber',
      label: 'Datum zamene registarskih tablica',
      type: 'date',
      minDate: dayjs().add(1, 'day'),
    },
    {
      name: 'typeApprovalMark',
      label: 'Homologacijska oznaka',
      type: 'text',
    },
    {
      name: 'engineDisplacement',
      label: 'Radna zapremina motora',
      type: 'number',
    },
    {
      name: 'enginePowerInKW',
      label: 'Snaga motora u KW',
      type: 'number',
    },
    {
      name: 'fuelType',
      label: 'Vrsta goriva ili pogona',
      type: 'text',
    },
    {
      name: 'seatingCapacity',
      label: 'Broj mesta za sedenje',
      type: 'number',
    },
    {
      name: 'standingCapacity',
      label: 'Broj mesta za stajanje',
      type: 'number',
    },
  ];

  const initialValues = useMemo(
    () => ({
      vehicleName: '',
      ['brand.id']: '',
      ['vehicleModel.id']: '',
      firstRegistration: '',
      licensePlateNumber: '',
      chassisNumber: '',
      ['driver.id']: '',
      vehicleType: '',
      maximumAuthorisedMass: '',
      mass: '',
      expiryDateOfLicensePlateNumber: '',
      typeApprovalMark: '',
      engineDisplacement: '',
      enginePowerInKW: '',
      fuelType: '',
      seatingCapacity: '',
      standingCapacity: '',
    }),
    []
  );

  const handleSubmit = (formData) => {
    const plateRegex = /^[A-Z]{2}[0-9]{3,4}[A-Z]{2}$/;

    if (!plateRegex.test(formData.licensePlateNumber)) {
      alert("Registarski broj mora biti u formatu AA999AA ili AA9999AA");
      return;
    }

    const payload = {
      vehicleName: formData.vehicleName,
      brand: { id: Number(formData['brand.id']) },
      vehicleModel: { id: Number(formData['vehicleModel.id']) },
      user: { id: Number(loggedUser?.id) },
      driver: { id: Number(formData['driver.id']) },
      vehicleType: formData.vehicleType,
      firstRegistration: dayjs(formData.firstRegistration).format('DD.MM.YYYY'),
      licensePlateNumber: formData.licensePlateNumber,
      chassisNumber: formData.chassisNumber,
      maximumAuthorisedMass: Number(formData.maximumAuthorisedMass),
      mass: Number(formData.mass),
      expiryDateOfLicensePlateNumber: dayjs(
        formData.expiryDateOfLicensePlateNumber
      ).format('DD.MM.YYYY'),
      typeApprovalMark: formData.typeApprovalMark,
      engineDisplacement: Number(formData.engineDisplacement),
      enginePowerInKW: Number(formData.enginePowerInKW),
      fuelType: formData.fuelType,
      seatingCapacity: Number(formData.seatingCapacity),
      standingCapacity: Number(formData.standingCapacity),
      fuelConsumptionPer100Kilometers: Number(
        formData.fuelConsumptionPer100Kilometers
      ),
    };

    handleAdd(payload);

    console.log(formData);
  };

  return (
    <ModalForm
      open={show}
      onClose={handleClose}
      onSubmit={handleSubmit}
      title={ModalForAddOrUpdateVehicle.titleForAddVehicle}
      fields={fields}
      initialValues={initialValues}
      error={error}
      submitLabel={SubmitButton.save}
      maxWidth="xl"
      grid
    />
  );
};

export default ModalForAddVehicle;
